import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
// import { InspectorModule } from './inspector.module';
import { TranslateModule } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';

interface ElementNode {
  tag: string;
  attributes: any;
  children: ElementNode[];
  bounds?: any;
  xpath?: string;
}

@Component({
  selector: 'app-tree-node',
  template: `
    <div class="tree-node" [class.selected]="isSelected" [attr.data-xpath]="node.xpath">
      <div
        class="node-header"
        (click)="onNodeClick()"
        (mouseenter)="onNodeHover()"
        [style.padding-left.px]="level * 16"
        >
        @if (hasChildren) {
          <span class="toggle-icon" (click)="toggleExpand($event)">
            <i [class.icon-chevron-right]="!isExpanded" [class.icon-chevron-down]="isExpanded"></i>
          </span>
        }
        <span class="node-icon">
          <i [class]="getNodeIcon()"></i>
        </span>
        <span class="node-tag">{{ node.tag }}</span>
        @if (getDisplayAttribute()) {
          <span class="node-attributes">
            {{ getDisplayAttribute() }}
          </span>
        }
      </div>
    
      @if (isExpanded && hasChildren) {
        <div class="node-children">
          @for (child of node.children; track child.xpath) {
            <app-tree-node
              [node]="child"
              [level]="level + 1"
              [selectedNode]="selectedNode"
              [expandAllFlag]="expandAllFlag"
              (nodeClick)="nodeClick.emit($event)"
              (nodeHover)="nodeHover.emit($event)"
            ></app-tree-node>
          }
        </div>
      }
    </div>
    `,
  styles: [`
    .tree-node {
      font-size: 12.5px;
      color: #444444;
    }

    .node-header {
      display: flex;
      align-items: center;
      padding: 3px 8px;
      cursor: pointer;
      border-radius: 4px;
      transition: background 0.12s;
      gap: 4px;
    }

    .node-header:hover {
      background: #f0f3fa;
    }

    .tree-node.selected > .node-header {
      background: #eef2fc;
      border-left: 2px solid #141d48;
      padding-left: 6px;
      color: #141d48;
    }

    .toggle-icon {
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 2px;
      color: #aaaaaa;
      font-size: 10px;
      flex-shrink: 0;
      transition: color 0.12s;
    }

    .toggle-icon:hover {
      color: #555555;
    }

    .node-icon {
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 5px;
      color: #141d48;
      font-size: 11px;
      flex-shrink: 0;
      opacity: 0.55;
    }

    .node-tag {
      color: #141d48;
      font-weight: 600;
      font-size: 12px;
    }

    .node-attributes {
      margin-left: 6px;
      color: #167FFC;
      font-size: 11px;
      opacity: 0.85;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 140px;
    }

    .node-children {
      /* Children indented by parent padding-left */
    }
  `],
  standalone: true,
  imports: [TranslateModule, FormsModule]
})
export class TreeNodeComponent implements OnChanges {
  @Input() expandAllFlag: boolean | null = null;
  @Input() node!: ElementNode;
  @Input() level: number = 0;
  @Input() selectedNode: ElementNode | null = null;
  @Output() nodeClick = new EventEmitter<ElementNode>();
  @Output() nodeHover = new EventEmitter<ElementNode>();

  isExpanded: boolean = true;

  get hasChildren(): boolean {
    // return this.node.children && this.node.children.length > 0;
    return Array.isArray(this.node.children) && this.node.children.length > 0;
  }

  get isSelected(): boolean {
    return this.selectedNode === this.node;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expandAllFlag'] && this.expandAllFlag !== null) {
      this.isExpanded = this.expandAllFlag;
    }
  }

  toggleExpand(event: Event): void {
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;
  }

  onNodeClick(): void {
    this.nodeClick.emit(this.node);
  }

  onNodeHover(): void {
    this.nodeHover.emit(this.node);
  }

  getDisplayAttribute(): string {
    const attrs = this.node.attributes;

    if (attrs['resource-id']) {
      return `#${attrs['resource-id'].split('/').pop()}`;
    }

    if (attrs.text && attrs.text.length < 30) {
      return `"${attrs.text}"`;
    }

    if (attrs['content-desc']) {
      return `[${attrs['content-desc']}]`;
    }

    return '';
  }

  getNodeIcon(): string {
    const tag = this.node.tag.toLowerCase();

    if (tag.includes('button')) return 'icon-button';
    if (tag.includes('text') || tag.includes('edit')) return 'icon-text';
    if (tag.includes('image') || tag.includes('icon')) return 'icon-image';
    if (tag.includes('layout') || tag.includes('view')) return 'icon-layout';
    if (tag.includes('list')) return 'icon-list';

    return 'icon-element';
  }
}